import { Button as AntButton } from 'antd'
import { ReactNode } from 'react'

export interface IconButtonProps {
  /**
   * Icon to display
   */
  icon: ReactNode

  /**
   * Click handler
   */
  onClick?: () => void

  /**
   * Disabled state
   */
  disabled?: boolean

  /**
   * Danger mode (for destructive actions)
   */
  danger?: boolean

  /**
   * Size of the button
   * @default 'medium'
   */
  size?: 'small' | 'medium'

  /**
   * Accessible label for screen readers (required)
   */
  ariaLabel: string

  /**
   * Tooltip text
   */
  title?: string

  /**
   * Additional CSS classes
   */
  className?: string
}

/**
 * IconButton Component
 *
 * A button component that displays only an icon, typically used for actions in tables,
 * toolbars, or compact layouts.
 *
 * @example
 * <IconButton
 *   icon={<EditOutlined />}
 *   onClick={handleEdit}
 *   ariaLabel="Edit item"
 *   title="Edit"
 * />
 *
 * @example
 * // Danger icon button
 * <IconButton
 *   icon={<DeleteOutlined />}
 *   onClick={handleDelete}
 *   danger
 *   ariaLabel="Delete item"
 * />
 */
export function IconButton({
  icon,
  size = 'small',
  ariaLabel,
  onClick,
  disabled = false,
  danger = false,
  title,
  className = '',
}: IconButtonProps) {
  const antSize = size === 'medium' ? 'middle' : 'small'

  return (
    <AntButton
      type="text"
      icon={icon}
      size={antSize}
      onClick={onClick}
      disabled={disabled}
      danger={danger}
      aria-label={ariaLabel}
      title={title || ariaLabel}
      className={className}
    />
  )
}
